import React, { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import {
  Grid,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Button,
  CircularProgress,
  Typography,
  Autocomplete
} from '@mui/material';
import AttachmentIcon from '@mui/icons-material/Attachment';
import httpRequest from "../../httpRequest";
import axios from "axios";
import { createCanvas, loadImage } from "canvas";
const PDFJS = require("pdfjs-dist/webpack");

const BulkExpense = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [expenses, setExpenses] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const [newExpense, setNewExpense] = useState({
    expense_name: "",
    expense_description: "",
    expense_amount: "",
    currency: "",
    comments: "",
    file: null,
    company_id: "",
    function_id: "",
    expense_category_id: ""
  });

  const [multiExpenses, setMultiExpenses] = useState([]); // For multi-file scanned expenses
  const [isScanning, setIsScanning] = useState(false);
  const [employeeCode, setEmployeeCode] = useState('');
  const [positionId, setPositionId] = useState('');

  const fetchUserData = async () => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/@me`);
      if (response.data) {
        const { employee_code } = response.data;
        setEmployeeCode(employee_code);

        const employeeResponse = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-employees?employee_code=${employee_code}`);
        const employeeData = employeeResponse.data.employees[0]; 
        setPositionId(employeeData.position_id);

        setNewExpense((prevExpense) => ({
          ...prevExpense,
          company_id: employeeData.company_id,
          function_id: employeeData.function_id,
        }));

        const [companiesResponse, functionsResponse] = await Promise.all([
          httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-companies`),
          httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/functions`),
        ]);

        const companiesData = companiesResponse.data.companies.filter(company =>
          employeeData.companies.includes(company.id)
        );
        setCompanies(companiesData);

        const functionsData = functionsResponse.data.functions.filter(func =>
          employeeData.functions.includes(func.id)
        );
        setFunctions(functionsData);

        fetchExpenses(employee_code);
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      enqueueSnackbar("Error fetching user data", { variant: "error" });
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/categories`);
        setCategories(response.data.categories);
      } catch (error) {
        console.error("Error fetching categories:", error);
        enqueueSnackbar("Error fetching categories", { variant: "error" });
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  const fetchExpenses = async (employeeCode) => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-expenses?employee_code=${employeeCode}`);
      setExpenses(response.data.expenses);
    } catch (error) {
      console.error("Error fetching expenses:", error);
      enqueueSnackbar("Error fetching expenses", { variant: "error" });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewExpense({ ...newExpense, file });
    }
  };

  const handleMultipleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setMultiExpenses(files.map(file => ({
      file,
      expense_name: "",
      expense_description: "",
      expense_amount: "",
      currency: "",
      expense_category_id: "",
      company_id: newExpense.company_id,
      function_id: newExpense.function_id,
    })));
  };

  const handleInputChange = (e, index = -1) => {
    const { name, value } = e.target;
    if (index === -1) {
      setNewExpense({ ...newExpense, [name]: value });
    } else {
      const updatedMultiExpenses = [...multiExpenses];
      updatedMultiExpenses[index] = { ...updatedMultiExpenses[index], [name]: value };
      setMultiExpenses(updatedMultiExpenses);
    }
  };

  const scanDocument = async (index = -1) => {
    const expenseToScan = index === -1 ? newExpense : multiExpenses[index];
    const { file } = expenseToScan;

    if (!file) {
      enqueueSnackbar("Please upload a file before scanning", { variant: "warning" });
      return;
    }

    setIsScanning(true);
    const convertPdfToImage = async (pdfFile) => {
      const pdfData = await pdfFile.arrayBuffer();
      const pdf = await PDFJS.getDocument({ data: pdfData }).promise;

      const images = [];
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 2.0 });

        const canvas = createCanvas(viewport.width, viewport.height);
        const context = canvas.getContext('2d');
        const renderContext = { canvasContext: context, viewport };

        await page.render(renderContext).promise;
        images.push(canvas.toDataURL("image/png"));
      }
      return images;
    };

    const joinImages = async (images) => {
      const imageElements = await Promise.all(images.map(src => loadImage(src)));

      const totalHeight = imageElements.reduce((acc, img) => acc + img.height, 0);
      const maxWidth = Math.max(...imageElements.map(img => img.width));

      const canvas = createCanvas(maxWidth, totalHeight);
      const context = canvas.getContext('2d');

      let currentY = 0;
      imageElements.forEach(img => {
        context.drawImage(img, 0, currentY, img.width, img.height);
        currentY += img.height;
      });

      return canvas.toDataURL("image/png");
    };

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        let base64Data;

        if (file.type === "application/pdf") {
          const images = await convertPdfToImage(file);
          base64Data = (await joinImages(images)).split(",")[1];
        } else {
          base64Data = reader.result.split(",")[1];
        }

        const response = await axios.post("https://api.openai.com/v1/chat/completions", {
          model: "gpt-4o-mini",
          messages: [
            {
              role: "user",
              content: [
                {
                  type: "text",
                  text: `Analyze this expense document and return structured data in JSON format: { expense_category: '', expense_name: '', expense_description: '', expense_amount: '', currency: '' }`,
                },
                {
                  type: "image_url",
                  image_url: {
                    url: `data:image/png;base64,${base64Data}`,
                  },
                },
              ],
            },
          ],
          max_tokens: 9000,
        }, {
          headers: {
            'Authorization': `Bearer sk-proj-j3YasCgXEuYlEGcSq3tZT3BlbkFJ7hEq6w26R5SQo5QvbVAD`,
            'Content-Type': 'application/json',
          },
        });

        const parsedData = JSON.parse(response.data.choices[0].message.content);
        const matchedCategoryId = categories.find(category => category.expense_category === parsedData.expense_category)?.id;

        if (index === -1) {
          setNewExpense(prev => ({
            ...prev,
            expense_category_id: matchedCategoryId || null,
            ...parsedData,
          }));
        } else {
          const updatedMultiExpenses = [...multiExpenses];
          updatedMultiExpenses[index] = {
            ...updatedMultiExpenses[index],
            expense_category_id: matchedCategoryId || null,
            ...parsedData,
          };
          setMultiExpenses(updatedMultiExpenses);
        }
        enqueueSnackbar("Document scanned successfully", { variant: "success" });
      } catch (error) {
        enqueueSnackbar("Error scanning document", { variant: "error" });
        console.error("Error scanning document:", error);
      } finally {
        setIsScanning(false);
      }
    };

    if (file.type === "application/pdf") {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsDataURL(file);
    }
  };

  const addSingleExpense = async () => {
    try {
      const formData = new FormData();
      formData.append('employee_code', employeeCode);
      formData.append('position_id', positionId);
      formData.append('expense_name', newExpense.expense_name);
      formData.append('expense_description', newExpense.expense_description);
      formData.append('expense_amount', newExpense.expense_amount);
      formData.append('currency', newExpense.currency);
      formData.append('comments', newExpense.comments);
      formData.append('file', newExpense.file);
      formData.append('company_id', newExpense.company_id);
      formData.append('function_id', newExpense.function_id);
      formData.append('expense_category_id', newExpense.expense_category_id);

      await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/add-expense`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      enqueueSnackbar('Expense added successfully', { variant: 'success' });
      setNewExpense({
        expense_name: '',
        expense_description: '',
        expense_amount: '',
        currency: '',
        comments: '',
        file: null,
        company_id: '',
        function_id: '',
        expense_category_id: ''
      });
      fetchExpenses(employeeCode);
    } catch (error) {
      console.error('Error adding expense:', error);
      enqueueSnackbar('Error adding expense', { variant: 'error' });
    }
  };

  const addMultipleExpenses = async () => {
    try {
        const formData = new FormData();

        // Add general data common for all expenses
        formData.append("employee_code", "EMP123");  // Example employee code
        formData.append("position_id", "POS001");    // Example position ID
        formData.append("scan_date", new Date().toISOString());

        // Append each individual child expense and its files
        multiExpenses.forEach((expense, index) => {
            formData.append(`pdf_files_${index}`, expense.file);  // Assuming it's a PDF file
            formData.append(`image_files_${index}`, expense.file);  // Assuming it's an image file
            formData.append(`expense_name_${index}`, expense.expense_name);
            formData.append(`expense_description_${index}`, expense.expense_description);
            formData.append(`expense_amount_${index}`, expense.expense_amount);
            formData.append(`currency_${index}`, expense.currency);
            formData.append(`expense_category_id_${index}`, expense.expense_category_id);
        });

        await httpRequest.post("/api/add-expense", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });

        enqueueSnackbar("Bulk expenses added successfully", { variant: "success" });
        setMultiExpenses([]);
    } catch (error) {
        enqueueSnackbar("Error adding bulk expenses", { variant: "error" });
    }
};


  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Paper elevation={3}>
          <Box p={2}>
            <Typography variant="h6">Add Single Expense</Typography>
            <TextField
              label="Expense Name"
              name="expense_name"
              value={newExpense.expense_name}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Description"
              name="expense_description"
              value={newExpense.expense_description}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Amount"
              name="expense_amount"
              value={newExpense.expense_amount}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Currency"
              name="currency"
              value={newExpense.currency}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />
            <Autocomplete
              options={categories}
              getOptionLabel={(option) => option.expense_category || ""}
              onChange={(e, newValue) =>
                setNewExpense((prev) => ({ ...prev, expense_category_id: newValue?.id }))
              }
              renderInput={(params) => (
                <TextField {...params} label="Expense Category" margin="normal" fullWidth />
              )}
            />
            <input type="file" onChange={handleFileChange} />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AttachmentIcon />}
              onClick={() => scanDocument()}
              disabled={isScanning}
            >
              {isScanning ? <CircularProgress size={24} /> : "Scan Document"}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={addSingleExpense}
              disabled={isScanning}
              style={{ marginTop: "10px" }}
            >
              Add Expense
            </Button>
          </Box>
        </Paper>
      </Grid>

      {/* Multiple Expenses Upload Section */}
      <Grid item xs={12} md={6}>
        <Paper elevation={3}>
          <Box p={2}>
            <Typography variant="h6">Add Multiple Expenses</Typography>
            <input
              type="file"
              multiple
              onChange={handleMultipleFileChange}
              accept="application/pdf,image/png,image/jpeg"
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AttachmentIcon />}
              onClick={() => multiExpenses.forEach((_, index) => scanDocument(index))}
              disabled={isScanning}
              style={{ marginTop: "10px" }}
            >
              {isScanning ? <CircularProgress size={24} /> : "Scan All Documents"}
            </Button>

            {/* Render Multi-Expense Table */}
            <TableContainer component={Paper} style={{ marginTop: "20px" }}>
              <Table>
                <TableBody>
                  {multiExpenses.map((expense, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          label="Expense Name"
                          name="expense_name"
                          value={expense.expense_name}
                          onChange={(e) => handleInputChange(e, index)}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          label="Description"
                          name="expense_description"
                          value={expense.expense_description}
                          onChange={(e) => handleInputChange(e, index)}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          label="Amount"
                          name="expense_amount"
                          value={expense.expense_amount}
                          onChange={(e) => handleInputChange(e, index)}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          label="Currency"
                          name="currency"
                          value={expense.currency}
                          onChange={(e) => handleInputChange(e, index)}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          options={categories}
                          getOptionLabel={(option) => option.expense_category || ""}
                          onChange={(e, newValue) =>
                            handleInputChange({ target: { name: "expense_category_id", value: newValue?.id } }, index)
                          }
                          renderInput={(params) => (
                            <TextField {...params} label="Expense Category" margin="normal" fullWidth />
                          )}
                          value={categories.find((cat) => cat.id === expense.expense_category_id) || null}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Button
              variant="contained"
              color="primary"
              onClick={addMultipleExpenses}
              disabled={isScanning || multiExpenses.length === 0}
              style={{ marginTop: "10px" }}
            >
              Add All Expenses
            </Button>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default BulkExpense;
